/**
 * Date handling for role assignments that start or end on a given day.
 *
 * A start date grants from the first moment of that day and an end date lasts through its last second,
 * both in the project's (or customer's) time zone, so "the 15th" means the whole 15th wherever the work
 * is. The browser's own zone is the fallback. No pict / DOM / network dependencies.
 */

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Whether a string names an IANA time zone this browser knows.
 *
 * @param {string} pTimeZone - ex. "America/Chicago".
 *
 * @return {boolean} true when Intl accepts it.
 */
function isValidTimeZone(pTimeZone)
{
	if (!pTimeZone || (typeof pTimeZone !== 'string'))
	{
		return false;
	}
	try
	{
		new Intl.DateTimeFormat('en-US', { timeZone: pTimeZone });
		return true;
	}
	catch (pError)
	{
		return false;
	}
}

/**
 * The zone to use: the one given when valid, else the browser's.
 *
 * @param {string} [pTimeZone] - Preferred IANA zone.
 *
 * @return {string} an IANA zone.
 */
function resolveTimeZone(pTimeZone)
{
	return isValidTimeZone(pTimeZone) ? pTimeZone : new Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Wall-clock parts of an instant in a zone.
 *
 * @param {number} pTime - Epoch milliseconds.
 * @param {string} pTimeZone - IANA zone.
 *
 * @return {{ Year: number, Month: number, Day: number, Hour: number, Minute: number, Second: number }} the parts.
 */
function getZonedParts(pTime, pTimeZone)
{
	const tmpParts = {};
	new Intl.DateTimeFormat('en-US',
		{
			timeZone: pTimeZone, hourCycle: 'h23',
			year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
		})
		.formatToParts(new Date(pTime))
		.forEach((pPart) =>
		{
			tmpParts[pPart.type] = Number(pPart.value);
		});
	return { Year: tmpParts.year, Month: tmpParts.month, Day: tmpParts.day, Hour: tmpParts.hour, Minute: tmpParts.minute, Second: tmpParts.second };
}

/**
 * The instant at which a zone's clocks read a given wall-clock time.
 *
 * Corrected twice so a day that crosses a daylight saving change still lands on the right hour.
 *
 * @param {Array<number>} pWallClock - [ year, month (1-12), day, hour, minute, second ].
 * @param {string} pTimeZone - IANA zone.
 *
 * @return {number} epoch milliseconds.
 */
function zonedWallClockToTime(pWallClock, pTimeZone)
{
	const tmpAsUTC = Date.UTC(pWallClock[0], pWallClock[1] - 1, pWallClock[2], pWallClock[3], pWallClock[4], pWallClock[5]);
	let tmpTime = tmpAsUTC;
	for (let i = 0; i < 2; i++)
	{
		const tmpParts = getZonedParts(tmpTime, pTimeZone);
		const tmpOffset = Date.UTC(tmpParts.Year, tmpParts.Month - 1, tmpParts.Day, tmpParts.Hour, tmpParts.Minute, tmpParts.Second) - tmpTime;
		tmpTime = tmpAsUTC - tmpOffset;
	}
	return tmpTime;
}

/**
 * Convert a picked day to the instant it begins or ends in a zone.
 *
 * @param {string} pDay - "YYYY-MM-DD" from a date input.
 * @param {string} [pTimeZone] - IANA zone; the browser's when missing.
 * @param {boolean} pEndOfDay - true for 23:59:59, false for 00:00:00.
 *
 * @return {string} an ISO instant, or '' when the day is empty or malformed.
 */
function dayToInstant(pDay, pTimeZone, pEndOfDay)
{
	const tmpMatch = DAY_PATTERN.exec(String(pDay || ''));
	if (!tmpMatch)
	{
		return '';
	}
	const tmpTime = pEndOfDay ?
		[ Number(tmpMatch[1]), Number(tmpMatch[2]), Number(tmpMatch[3]), 23, 59, 59 ] :
		[ Number(tmpMatch[1]), Number(tmpMatch[2]), Number(tmpMatch[3]), 0, 0, 0 ];
	return new Date(zonedWallClockToTime(tmpTime, resolveTimeZone(pTimeZone))).toISOString();
}

/**
 * The instant a role starting on this day takes effect.
 *
 * @param {string} pDay - "YYYY-MM-DD".
 * @param {string} [pTimeZone] - IANA zone.
 *
 * @return {string} an ISO instant, or ''.
 */
function dayStartToInstant(pDay, pTimeZone)
{
	return dayToInstant(pDay, pTimeZone, false);
}

/**
 * The last instant of a role ending on this day.
 *
 * @param {string} pDay - "YYYY-MM-DD".
 * @param {string} [pTimeZone] - IANA zone.
 *
 * @return {string} an ISO instant, or ''.
 */
function dayEndToInstant(pDay, pTimeZone)
{
	return dayToInstant(pDay, pTimeZone, true);
}

/**
 * The day an instant falls on in a zone.
 *
 * @param {any} pInstant - ISO string or Date; empty and unparseable values read as unset.
 * @param {string} [pTimeZone] - IANA zone.
 *
 * @return {string} "YYYY-MM-DD", or ''.
 */
function instantToDay(pInstant, pTimeZone)
{
	const tmpTime = pInstant ? new Date(pInstant).getTime() : NaN;
	if (!Number.isFinite(tmpTime))
	{
		return '';
	}
	const tmpParts = getZonedParts(tmpTime, resolveTimeZone(pTimeZone));
	return `${tmpParts.Year}-${String(tmpParts.Month).padStart(2, '0')}-${String(tmpParts.Day).padStart(2, '0')}`;
}

/**
 * Today in a zone, for a date input's `min`.
 *
 * @param {string} [pTimeZone] - IANA zone.
 *
 * @return {string} "YYYY-MM-DD".
 */
function todayIn(pTimeZone)
{
	return instantToDay(new Date(), pTimeZone);
}

/**
 * Where an assignment stands in its window: StartDate inclusive, ExpirationDate exclusive, expiry wins.
 *
 * Same rule as api/permissions source/lib/UserContextRole-Window.js, which decides what the user can do;
 * this only labels it.
 *
 * @param {any} pStartDate - ISO string, or empty.
 * @param {any} pExpirationDate - ISO string, or empty.
 * @param {Date} [pNow] - The moment to evaluate at.
 *
 * @return {'Scheduled'|'Active'|'Expired'} the state.
 */
function getWindowState(pStartDate, pExpirationDate, pNow = new Date())
{
	const tmpNow = pNow.getTime();
	const tmpExpiration = pExpirationDate ? new Date(pExpirationDate).getTime() : NaN;
	if (Number.isFinite(tmpExpiration) && (tmpExpiration <= tmpNow))
	{
		return 'Expired';
	}
	const tmpStart = pStartDate ? new Date(pStartDate).getTime() : NaN;
	if (Number.isFinite(tmpStart) && (tmpStart > tmpNow))
	{
		return 'Scheduled';
	}
	return 'Active';
}

module.exports = { isValidTimeZone, resolveTimeZone, dayStartToInstant, dayEndToInstant, instantToDay, todayIn, getWindowState };
